import { TransportOutput, UpdateTripTransportInput } from '@allocab/allocab-commons-shared-types';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import { useState } from 'react';

import { PickupDelay } from './PickupDelay';
import { t } from '../../../services/translation/translation';
import { formatDateForParisTZ } from '../../../utils/date';
import { GradientButton } from '../../button/GradientButton';
import { TransparentButton } from '../../button/TransparentButton';

type Props = {
  trip: TripOutput;
  transport: TransportOutput;
  onClose: () => void;
  onUpdate: (_input: UpdateTripTransportInput) => void;
  isLoading: boolean;
};

export const TransportPickerStep2 = ({ transport, onClose, onUpdate, isLoading, trip }: Props) => {
  const stationWithSign = trip.tripVars.tripType === 'FARETYPE_STATION' && trip.tripVars.reception;
  const minimalDelay = stationWithSign
    ? 0
    : transport.pickupPoint.minimalDelay
    ? transport.pickupPoint.minimalDelay
    : transport.type === 'trainstation'
    ? 10
    : 15;
  const [delay, setDelay] = useState(minimalDelay);
  const addDelay = delay * 60 * 1000;
  const newArrivalTime = formatDateForParisTZ(transport.arrival + addDelay, 'YYYY-MM-DD à HH:mm');

  const handleConfirm = () => {
    onUpdate({
      transport: {
        transportCarrier: transport.type === 'airport' ? transport.carrier : undefined,
        transportNumber: transport.transportNumber,
        scheduledDeparture: transport.arrival + addDelay,
        scheduledArrival: transport.arrival,
        arrival: {
          name: transport.arrivalLocation.name,
          iata: transport.arrivalLocation.iata,
          position: { lng: transport.arrivalLocation.lng, lat: transport.arrivalLocation.lat },
        },
        departure: {
          name: transport.departureLocation.name,
          iata: transport.departureLocation.iata,
          position: { lng: transport.departureLocation.lng, lat: transport.departureLocation.lat },
        },
      },
      delayToKeep: delay,
      newAddress: transport.pickupPoint.formattedAddress,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex">
        <div>
          {transport.type === 'trainstation'
            ? t('transport-picker.train-picker.train-number')
            : t('transport-picker.fligh-picker.flight-number')}
        </div>
        <div className="uppercase flex items-center rounded-full bg-orange-600 ml-2 p-1">
          <div className="text-white text-sm">
            {transport.type === 'trainstation'
              ? transport.transportNumber
              : transport.carrier + ' ' + transport.transportNumber}
          </div>
        </div>
      </div>
      {stationWithSign ? null : (
        <>
          <div>
            <PickupDelay transport={transport} delay={delay} onDelayUpdated={setDelay} />
          </div>
          <div>
            <div className="font-bold">{t('transport-picker.advice')} :</div>
            <div className="text-sm">
              {t(
                transport.type === 'trainstation'
                  ? 'transport-picker.train-picker.offset-advice'
                  : 'transport-picker.fligh-picker.offset-advice',
                {
                  minimalDelay: minimalDelay,
                },
              )}
            </div>
          </div>
        </>
      )}
      <div className="grid border border-black rounded p-2">
        <div className="font-bold">{t('transport-picker.new-date-pickup')}</div>
        <div className="font-bold text-orange-600 ">{newArrivalTime}</div>
      </div>
      <div className="grid border border-black rounded p-2">
        <div className="font-bold">{t('transport-picker.new-address-pickup')}</div>
        <div className="font-bold text-orange-600 ">
          {transport.pickupPoint.formattedAddress
            ? transport.pickupPoint.formattedAddress
            : trip.resume.start.formattedAddress}
        </div>
      </div>
      <div className="grid">
        <div className="font-bold">{t('transport-picker.passenger-text')}</div>
        <div className="flex">
          {trip.tripVars.reception ? transport.pickupPoint.signDescription : transport.pickupPoint.description}
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-5">
        <TransparentButton onClick={onClose}>{t('common.close')}</TransparentButton>
        <GradientButton onClick={handleConfirm} isLoading={isLoading}>
          {t('common.update')}
        </GradientButton>
      </div>
    </div>
  );
};
